/**
 * @file Chapeaux des portraits de skins en SVG genere (remplacent les emojis : rendu identique sur tous les appareils,
 * pas de « tofu » sur les vieux Android). Boite 40x26, posee au-dessus de la tete du portrait CSS (.skin-cat).
 */
import type { HatId } from '@config/types';

const NS = 'http://www.w3.org/2000/svg';
const STROKE = '#1f1638';

type SvgAttrs = Record<string, string | number>;
const s = (tag: string, attrs: SvgAttrs = {}): SVGElement => {
  const e = document.createElementNS(NS, tag);
  for (const [k, v] of Object.entries(attrs)) e.setAttribute(k, String(v));
  return e;
};
const o = (fill: string, extra: SvgAttrs = {}): SvgAttrs => ({ fill, stroke: STROKE, 'stroke-width': 2, 'stroke-linejoin': 'round', 'stroke-linecap': 'round', ...extra });
const shine = (d: string): SVGElement => s('path', { d, fill: 'none', stroke: '#ffffff', 'stroke-width': 1.6, 'stroke-linecap': 'round', opacity: 0.75 });

const crown = (gold: string, gem: string, tall: number): SVGElement[] => [
  s('path', o(gold, { d: `M9 23l-2-${tall}l7 5 6-${tall + 2} 6 ${tall + 2} 7-5-2 ${tall}z` })),
  s('circle', o(gem, { cx: 20, cy: 18, r: 2.2, 'stroke-width': 1.4 })),
  shine('M11 20.5h4'),
];

const HATS: Record<Exclude<HatId, 'none'>, () => SVGElement[]> = {
  maid: () => [
    s('path', o('#ff4f7b', { d: 'M20 15l-10-7v14zM20 15l10-7v14z' })),
    s('rect', o('#ff7b9c', { x: 17, y: 12, width: 6, height: 6, rx: 2 })),
  ],
  sailor: () => [
    s('path', o('#ffffff', { d: 'M8 21c0-7 5-11 12-11s12 4 12 11z' })),
    s('rect', o('#2f5bd3', { x: 7, y: 19, width: 26, height: 5, rx: 2 })),
  ],
  chef: () => [
    s('path', o('#ffffff', { d: 'M12 18c-5 0-6-7-1-8 0-5 7-7 9-3 2-4 9-2 9 3 5 1 4 8-1 8z' })),
    s('rect', o('#f1f1f1', { x: 12, y: 17, width: 16, height: 7, rx: 1.5 })),
  ],
  nurse: () => [
    s('path', o('#ffffff', { d: 'M9 23l2-11h18l2 11z' })),
    s('path', { d: 'M20 14v7M16.5 17.5h7', stroke: '#ff4f5e', 'stroke-width': 2.6, 'stroke-linecap': 'round' }),
  ],
  police: () => [
    s('path', o('#2a3f8f', { d: 'M8 18c0-6 5-10 12-10s12 4 12 10z' })),
    s('path', o('#1f1638', { d: 'M7 18h26l-3 5H10z' })),
    s('circle', o('#ffc83d', { cx: 20, cy: 13, r: 2.6, 'stroke-width': 1.4 })),
  ],
  detective: () => [
    s('path', o('#a77b4f', { d: 'M8 20c0-8 5-12 12-12s12 4 12 12z' })),
    s('path', o('#8a6440', { d: 'M4 22c4-3 28-3 32 0-4 2-28 2-32 0z' })),
    s('path', { d: 'M12 12c5 2 11 2 16 0', fill: 'none', stroke: STROKE, 'stroke-width': 1.4, 'stroke-dasharray': '2 1.6' }),
  ],
  witch: () => [
    s('path', o('#6b3fb5', { d: 'M12 21L22 1l1 9 5 11z' })),
    s('ellipse', o('#6b3fb5', { cx: 20, cy: 22, rx: 16, ry: 3 })),
    s('rect', o('#ffc83d', { x: 15.5, y: 16.5, width: 8, height: 4, rx: 1, 'stroke-width': 1.4 })),
  ],
  tiara: () => [
    s('path', o('#dfe6f5', { d: 'M8 23c1-5 5-8 8-8l4-6 4 6c3 0 7 3 8 8-4-2-8-3-12-3s-8 1-12 3z' })),
    s('circle', o('#ff7bc0', { cx: 20, cy: 15.5, r: 2.2, 'stroke-width': 1.4 })),
    shine('M11 20c1.5-1.5 3-2.3 4.5-2.6'),
  ],
  cowboy: () => [
    s('path', o('#b07a45', { d: 'M12 19c0-7 2-11 4-11 2 0 2.5 2 4 2s2-2 4-2c2 0 4 4 4 11z' })),
    s('path', o('#8a5a32', { d: 'M3 17c3 6 31 6 34 0-3 3-31 3-34 0z' })),
    s('rect', { x: 12, y: 16, width: 16, height: 2.4, fill: '#5a3a20' }),
  ],
  captain: () => [
    s('path', o('#ffffff', { d: 'M7 17c0-6 6-9 13-9s13 3 13 9z' })),
    s('path', o('#1f1638', { d: 'M8 17h24l-3 6H11z' })),
    s('path', { d: 'M20 10.5v5M17.5 12h5M17 14.5c1 1.5 5 1.5 6 0', fill: 'none', stroke: '#e0a800', 'stroke-width': 1.6, 'stroke-linecap': 'round' }),
  ],
  wizard: () => [
    s('path', o('#2f5bd3', { d: 'M11 23L20 0l9 23z' })),
    s('ellipse', o('#2f5bd3', { cx: 20, cy: 23, rx: 13, ry: 2.6 })),
    s('path', { d: 'M18 9l1 2 2 .3-1.5 1.4.4 2.1-1.9-1-1.9 1 .4-2.1L15 11.3l2-.3z', fill: '#ffc83d' }),
    s('circle', { cx: 23, cy: 17, r: 1.3, fill: '#ffc83d' }),
  ],
  helmet: () => [
    s('path', o('#9aa7c7', { d: 'M7 23c0-9 6-14 13-14s13 5 13 14z' })),
    s('path', { d: 'M20 9v14', stroke: STROKE, 'stroke-width': 1.8 }),
    s('rect', o('#b9c4e0', { x: 6, y: 21, width: 28, height: 3.5, rx: 1.5 })),
    shine('M11 17a9 9 0 0 1 4-5'),
  ],
  pirate: () => [
    s('path', o('#2a2350', { d: 'M4 20c4-9 10-12 16-12s12 3 16 12c-5-2-10-3-16-3S9 18 4 20z' })),
    s('circle', { cx: 20, cy: 13, r: 2.6, fill: '#ffffff' }),
    s('path', { d: 'M16.5 17l7-2.5M16.5 14.5l7 2.5', stroke: '#ffffff', 'stroke-width': 1.3, 'stroke-linecap': 'round' }),
  ],
  bunny: () => [
    s('path', o('#ffffff', { d: 'M13 24c-4-6-4-17-1-21 3 2 4 13 4 21zM27 24c4-6 4-17 1-21-3 2-4 13-4 21z' })),
    s('path', { d: 'M13.5 20c-2-5-2-11-1-14 1.4 2 2 8 2 14zM26.5 20c2-5 2-11 1-14-1.4 2-2 8-2 14z', fill: '#ffb3d1' }),
  ],
  beanie: () => [
    s('path', o('#ff5a5a', { d: 'M8 21c0-8 5-12 12-12s12 4 12 12z' })),
    s('rect', o('#d63a3a', { x: 7, y: 19, width: 26, height: 5, rx: 2 })),
    s('circle', o('#ffffff', { cx: 20, cy: 7.5, r: 3.5 })),
  ],
  headphones: () => [
    s('path', { d: 'M8 22c0-10 5-15 12-15s12 5 12 15', fill: 'none', stroke: STROKE, 'stroke-width': 5, 'stroke-linecap': 'round' }),
    s('path', { d: 'M8 22c0-10 5-15 12-15s12 5 12 15', fill: 'none', stroke: '#b26bff', 'stroke-width': 2.4, 'stroke-linecap': 'round' }),
    s('rect', o('#ff4f7b', { x: 3, y: 17, width: 8, height: 9, rx: 3 })),
    s('rect', o('#ff4f7b', { x: 29, y: 17, width: 8, height: 9, rx: 3 })),
  ],
  crown: () => crown('#ffc83d', '#ff4f5e', 9),
};

/** SVG du chapeau `hat`, ou `null` pour 'none'. */
export const hatIcon = (hat: HatId, cls = 'hat-ico'): SVGSVGElement | null => {
  if (hat === 'none') return null;
  const svg = document.createElementNS(NS, 'svg');
  svg.setAttribute('viewBox', '0 0 40 26');
  svg.setAttribute('class', cls);
  svg.setAttribute('aria-hidden', 'true');
  svg.setAttribute('focusable', 'false');
  for (const k of HATS[hat]()) svg.append(k);
  return svg;
};
